import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ArchivoDescargado, archivoDeRespuesta } from '../utils/descarga-archivo';

/** Espejo de la respuesta de `GET api-externa-cuenta/estado` (fase 1 + limites de fase 2). */
export interface EstadoApiCuenta {
  habilitado: boolean;
  estado?: 'INVITADO' | 'ACTIVO' | 'DESHABILITADO';
  /** Ids del registry (`CLP_VES`, ...) o `["*"]`. */
  productos?: string[];
  limitePorMinuto?: number | null;
  maxLlaves?: number;
  invitadoEn?: string | null;
  activadoEn?: string | null;
  /** Solo viene si esta DESHABILITADO a mano por un operador. */
  motivo?: string | null;
  // ---- fase 2 (opcionales: el backend los agrega cuando los tenga)
  /** Cupo de `GET /saldos` por minuto (parametro API_EXTERNA_LIMITE_SALDOS_POR_MINUTO). */
  limiteSaldosPorMinuto?: number | null;
  /** IPs permitidas por llave (API_EXTERNA_MAX_IPS). */
  maxIps?: number | null;
  /** Vigencia de la llave en dias (API_EXTERNA_LLAVE_VIGENCIA_DIAS). */
  vigenciaLlaveDias?: number | null;
  /** IP publica desde la que el cliente esta navegando, para sugerirla al crear la llave. */
  ipActual?: string | null;
  /** Fase 3: entorno del backend que responde; decide que entorno Postman se baja. */
  entorno?: 'prod' | 'qa';
  /** Límite EFECTIVO (general o personalizado); si viene, manda sobre `limitePorMinuto`. */
  limite?: {
    porMinuto: number;
    saldosPorMinuto?: number | null;
    origen?: 'general' | 'personalizado';
    general?: number | null;
  } | null;
}

export interface DocumentacionApi {
  version: string;
  actualizadoEn: string;
  /** Fragmento HTML (sin `<html>`/`<body>` ni scripts). Se muestra con `[innerHTML]`. */
  html: string;
}

/** Fila de `api_externa_llave` tal como la ve su dueno (nunca trae la clave). */
export interface LlaveApi {
  id: string;
  kid: string;
  nombre: string;
  estado: 'ACTIVA' | 'REVOCADA';
  ips: string[];
  creadaEn: string | null;
  expiraEn: string | null;
  ultimoUsoEn: string | null;
  ultimoUsoIp: string | null;
  revocadaEn?: string | null;
  motivoRevocacion?: string | null;
}

/** `POST api-externa-cuenta/llaves/desafio`: texto de un solo uso a firmar. */
export interface DesafioLlave {
  desafio: string;
  expiraEn: string;
}

/** Firma Ed25519 (base64url sin `=`) del texto exacto del desafío. */
export interface PruebaPosesion {
  desafio: string;
  firma: string;
}

/** Lo que devuelve `GET api-externa-cuenta/llaves`: la activa (o ninguna) mas la IP del navegante. */
export interface LlavesRespuesta {
  llave: LlaveApi | null;
  ipActual?: string | null;
  maxIps?: number | null;
}

/** Fila de `api_externa_uso` (`GET api-externa-cuenta/uso?dias=7`). */
export interface FilaUsoApi {
  dia: string;
  ruta: string;
  ok: number;
  http401: number;
  http403: number;
  http429: number;
}

/**
 * La seccion «API» del dashboard del cliente. El usuario sale del JWT: no se
 * manda ningun id.
 *
 * `estado` y `documentacion` saltan el interceptor de errores: el primero se
 * consulta desde el navbar para decidir si se muestra el menu (ante error se
 * esconde, sin modal), y el segundo responde 403 a quien no esta habilitado,
 * que la pantalla muestra en su lugar y no como «Acceso denegado» flotante.
 *
 * Las llamadas de llaves (fase 2) tambien lo saltan: el 409 trae `codigo` y
 * `causas` que la pantalla muestra en su lugar. La clave privada NUNCA viaja
 * por aca: solo la publica en hex de 64.
 */
@Injectable({ providedIn: 'root' })
export class ApiExternaCuentaService {
  private baseUrl = `${environment.apiNest}api-externa-cuenta`;
  private readonly sinInterceptor = { headers: { 'X-Skip-Error-Interceptor': 'true' } };

  constructor(private http: HttpClient) {}

  estado(): Promise<EstadoApiCuenta> {
    return firstValueFrom(
      this.http.get<EstadoApiCuenta>(`${this.baseUrl}/estado`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  activar(): Promise<EstadoApiCuenta> {
    return firstValueFrom(this.http.post<EstadoApiCuenta>(`${this.baseUrl}/activar`, {}));
  }

  documentacion(): Promise<DocumentacionApi> {
    return firstValueFrom(
      this.http.get<DocumentacionApi>(`${this.baseUrl}/documentacion`, {
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  // ---------------------------------------------------------------- llaves (fase 2)

  /**
   * La llave ACTIVA del cliente, o `null`. Se tolera que el backend devuelva
   * el objeto envuelto (`{ llave, ipActual }`), la llave sola o una lista.
   */
  async llaves(): Promise<LlavesRespuesta> {
    const r = await firstValueFrom(
      this.http.get<unknown>(`${this.baseUrl}/llaves`, this.sinInterceptor)
    );
    return normalizarLlaves(r);
  }

  /**
   * Prueba de posesión: el back entrega un texto de un solo uso que hay que
   * firmar con la privada de la pública que se registra. Salta el interceptor
   * (el 429 por exceso de acciones se muestra en el diálogo).
   */
  desafio(): Promise<DesafioLlave> {
    return firstValueFrom(
      this.http.post<DesafioLlave>(`${this.baseUrl}/llaves/desafio`, {}, this.sinInterceptor)
    );
  }

  crearLlave(datos: { nombre: string; ips: string[]; clavePublica: string; prueba?: PruebaPosesion }): Promise<LlaveApi> {
    return firstValueFrom(
      this.http.post<LlaveApi>(`${this.baseUrl}/llaves`, datos, this.sinInterceptor)
    );
  }

  cambiarIps(llaveId: string, ips: string[]): Promise<LlaveApi> {
    return firstValueFrom(
      this.http.put<LlaveApi>(
        `${this.baseUrl}/llaves/${encodeURIComponent(llaveId)}/ips`,
        { ips },
        this.sinInterceptor
      )
    );
  }

  /** Revoca la actual y crea una nueva con el mismo nombre e IPs. Devuelve la nueva. */
  rotar(llaveId: string, clavePublica: string, prueba?: PruebaPosesion): Promise<LlaveApi> {
    return firstValueFrom(
      this.http.post<LlaveApi>(
        `${this.baseUrl}/llaves/${encodeURIComponent(llaveId)}/rotar`,
        prueba ? { clavePublica, prueba } : { clavePublica },
        this.sinInterceptor
      )
    );
  }

  revocar(llaveId: string, motivo?: string): Promise<LlaveApi> {
    const body = motivo?.trim() ? { motivo: motivo.trim() } : {};
    return firstValueFrom(
      this.http.post<LlaveApi>(
        `${this.baseUrl}/llaves/${encodeURIComponent(llaveId)}/revocar`,
        body,
        this.sinInterceptor
      )
    );
  }

  /**
   * El OpenAPI como archivo (`GET documentacion/openapi`). Salta el
   * interceptor: 404 = «no disponible», que la pantalla muestra en su lugar.
   */
  openapi(): Promise<Blob> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/documentacion/openapi`, {
        responseType: 'blob',
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  async uso(dias = 7): Promise<FilaUsoApi[]> {
    const r = await firstValueFrom(
      this.http.get<unknown>(`${this.baseUrl}/uso`, {
        params: new HttpParams().set('dias', dias),
        ...this.sinInterceptor,
      })
    );
    return normalizarUso(r);
  }

  // ---------------------------------------------------------------- fase 3: documentación

  /**
   * El OpenAPI como texto (mismo `GET documentacion/openapi`) para pintarlo con
   * Swagger UI en la pestaña «Swagger». Salta el interceptor: 404 = «todavía no
   * publicado», que la pestaña muestra en su lugar.
   */
  openapiTexto(): Promise<string> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/documentacion/openapi`, {
        responseType: 'text',
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
  }

  /** El OpenAPI como archivo, con el nombre que manda `Content-Disposition`. */
  openapiArchivo(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/openapi', 'aguacate-integraciones-v1.openapi.yaml');
  }

  /** Colección Postman del cliente (`GET documentacion/postman`). */
  postman(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/postman', 'aguacate-integraciones-v1.postman_collection.json');
  }

  /** Entorno Postman (`GET documentacion/postman-entorno/qa|prod`). */
  postmanEntorno(entorno: 'qa' | 'prod'): Promise<ArchivoDescargado> {
    return this.descargar(
      `documentacion/postman-entorno/${entorno}`,
      `aguacate-integraciones-v1.postman_environment.${entorno}.json`
    );
  }

  /**
   * Entorno Postman del ambiente que responde (`GET documentacion/postman-entorno`,
   * sin parametro): en prod el cliente solo ve el de prod. `postmanEntorno(qa|prod)`
   * queda sin uso desde el 03/11.
   */
  postmanEntornoActual(): Promise<ArchivoDescargado> {
    return this.descargar('documentacion/postman-entorno', 'aguacate-integraciones-v1.postman_environment.json');
  }

  /**
   * Baja un archivo con el token de siempre (lo pone el interceptor) y lee el
   * nombre de `Content-Disposition` (expuesto por CORS en el backend). Salta el
   * interceptor de errores: 404 = archivo todavía no publicado.
   */
  private async descargar(ruta: string, nombrePorDefecto: string): Promise<ArchivoDescargado> {
    const r = await firstValueFrom(
      this.http.get(`${this.baseUrl}/${ruta}`, {
        responseType: 'blob',
        observe: 'response',
        headers: { 'X-Skip-Error-Interceptor': 'true' },
      })
    );
    return archivoDeRespuesta(r, nombrePorDefecto);
  }
}

// ------------------------------------------------------------------ normalizadores

function esLlave(x: unknown): x is LlaveApi {
  return !!x && typeof x === 'object' && typeof (x as { kid?: unknown }).kid === 'string';
}

function normalizarLlaves(r: unknown): LlavesRespuesta {
  if (!r || typeof r !== 'object') return { llave: null };
  if (Array.isArray(r)) {
    const activa = r.find((x) => esLlave(x) && x.estado === 'ACTIVA') ?? r.find(esLlave) ?? null;
    return { llave: activa && activa.estado === 'ACTIVA' ? activa : null };
  }
  const o = r as { llave?: unknown; llaves?: unknown; ipActual?: unknown; maxIps?: unknown };
  if ('llave' in o || 'llaves' in o) {
    const interna = 'llave' in o ? normalizarLlaves(o.llave) : normalizarLlaves(o.llaves);
    return {
      llave: interna.llave,
      ipActual: typeof o.ipActual === 'string' ? o.ipActual : null,
      maxIps: typeof o.maxIps === 'number' ? o.maxIps : null,
    };
  }
  if (esLlave(o)) return { llave: o.estado === 'REVOCADA' ? null : o };
  return { llave: null };
}

function normalizarUso(r: unknown): FilaUsoApi[] {
  const lista = Array.isArray(r)
    ? r
    : (r as { uso?: unknown; items?: unknown; filas?: unknown })?.uso ??
      (r as { items?: unknown })?.items ??
      (r as { filas?: unknown })?.filas ??
      [];
  if (!Array.isArray(lista)) return [];
  return lista.map((f) => {
    const x = (f ?? {}) as Record<string, unknown>;
    return {
      dia: String(x['dia'] ?? ''),
      ruta: String(x['ruta'] ?? ''),
      ok: Number(x['ok'] ?? 0),
      http401: Number(x['http401'] ?? x['h401'] ?? 0),
      http403: Number(x['http403'] ?? x['h403'] ?? 0),
      http429: Number(x['http429'] ?? x['h429'] ?? 0),
    };
  });
}
